import { FC, useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Button, Form, Input, InputNumber, Modal, Select, Space, Switch, Table, Tag, message } from "antd";
import { useTranslation } from "react-i18next";
import aiChatAdminService from "src/module/aiChatAdmin/service/aiChatAdmin.service";
import { AiKnowledgeEntry } from "src/module/aiChatAdmin/entity/aiChatAdmin.entity";

const PAGE_SIZE = 15;

export const AiKnowledge: FC = () => {
	const { t } = useTranslation();
	const [form] = Form.useForm();
	const [list, setList] = useState<AiKnowledgeEntry[]>([]);
	const [loading, setLoading] = useState(false);
	const [keyword, setKeyword] = useState("");
	const [enabled, setEnabled] = useState<string>("all");
	const [page, setPage] = useState(1);
	const [modalVisible, setModalVisible] = useState(false);
	const [editing, setEditing] = useState<AiKnowledgeEntry>();

	const loadData = useCallback(async () => {
		setLoading(true);
		try {
			const result = await aiChatAdminService.listKnowledge({
				q: keyword,
				enabled: enabled === "all" ? undefined : enabled === "enabled",
				limit: PAGE_SIZE,
				offset: (page - 1) * PAGE_SIZE,
			});
			setList(result.items);
		} catch {
			setList([]);
		} finally {
			setLoading(false);
		}
	}, [enabled, keyword, page]);

	useEffect(() => {
		void loadData();
	}, [loadData]);

	const openCreate = () => {
		setEditing(undefined);
		form.resetFields();
		form.setFieldsValue({ enabled: true, priority: 0, source: "admin" });
		setModalVisible(true);
	};

	const openEdit = (record: AiKnowledgeEntry) => {
		setEditing(record);
		form.setFieldsValue({
			...record,
			tags: record.tags.join("，"),
		});
		setModalVisible(true);
	};

	const submit = async () => {
		const values = await form.validateFields();
		const payload = {
			...values,
			tags: String(values.tags || "")
				.split(/[,\s，、]+/)
				.map((item) => item.trim())
				.filter(Boolean),
		};
		if (editing) {
			await aiChatAdminService.updateKnowledge(editing.id, payload);
		} else {
			await aiChatAdminService.createKnowledge(payload);
		}
		message.success(t("operate_success"));
		setModalVisible(false);
		loadData();
	};

	const remove = (record: AiKnowledgeEntry) => {
		Modal.confirm({
			title: t("confirmOperation"),
			onOk: async () => {
				await aiChatAdminService.deleteKnowledge(record.id);
				message.success(t("operate_success"));
				loadData();
			},
		});
	};

	const columns = [
		{ title: "ID", dataIndex: "id", width: 80 },
		{ title: t("title"), dataIndex: "title", width: 220 },
		{
			title: "标签",
			dataIndex: "tags",
			width: 220,
			render: (tags: string[]) => tags?.map((tag) => <Tag key={tag}>{tag}</Tag>),
		},
		{ title: "来源", dataIndex: "source", width: 120 },
		{ title: "优先级", dataIndex: "priority", width: 100 },
		{
			title: t("status"),
			dataIndex: "enabled",
			width: 100,
			render: (value: boolean) => value ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>,
		},
		{ title: "更新时间", dataIndex: "updatedAt", width: 190 },
		{
			title: t("action"),
			width: 180,
			render: (_value: unknown, record: AiKnowledgeEntry) => (
				<Space>
					<Button type="primary" onClick={() => openEdit(record)}>{t("edit")}</Button>
					<Button danger onClick={() => remove(record)}>{t("delete")}</Button>
				</Space>
			),
		},
	];

	return (
		<Container>
			<Header>
				<Space wrap>
					<Input.Search
						allowClear
						placeholder="标题 / 内容 / 标签"
						value={keyword}
						onChange={(event) => setKeyword(event.target.value)}
						onSearch={() => {
							setPage(1);
							loadData();
						}}
						style={{ width: 260 }}
					/>
					<Select value={enabled} onChange={(value) => { setEnabled(value); setPage(1); }} style={{ width: 120 }}>
						<Select.Option value="all">{t("all")}</Select.Option>
						<Select.Option value="enabled">启用</Select.Option>
						<Select.Option value="disabled">停用</Select.Option>
					</Select>
					<Button type="primary" onClick={openCreate}>{t("add")}</Button>
				</Space>
			</Header>
			<Table
				loading={loading}
				columns={columns}
				dataSource={list}
				rowKey={(record) => record.id}
				pagination={false}
				scroll={{ x: 1100 }}
			/>
			<Pager>
				<Button disabled={page <= 1} onClick={() => setPage(page - 1)}>上一页</Button>
				<span>第 {page} 页，当前 {list.length} 条</span>
				<Button disabled={list.length < PAGE_SIZE} onClick={() => setPage(page + 1)}>下一页</Button>
			</Pager>
			<Modal
				title={editing ? "编辑 AI 补充知识" : "新增 AI 补充知识"}
				open={modalVisible}
				onOk={submit}
				onCancel={() => setModalVisible(false)}
				width={760}
				destroyOnClose
			>
				<Form form={form} layout="vertical">
					<Form.Item name="title" label={t("title")} rules={[{ required: true, message: "请输入标题" }]}>
						<Input maxLength={120} />
					</Form.Item>
					<Form.Item name="content" label="内容" rules={[{ required: true, message: "请输入内容" }]}>
						<Input.TextArea rows={8} maxLength={12000} showCount />
					</Form.Item>
					<Form.Item name="tags" label="标签">
						<Input placeholder="多个标签用逗号分隔" />
					</Form.Item>
					<Form.Item name="source" label="来源">
						<Input maxLength={80} />
					</Form.Item>
					<Form.Item name="priority" label="优先级">
						<InputNumber min={-1000} max={1000} />
					</Form.Item>
					<Form.Item name="enabled" label="是否启用" valuePropName="checked">
						<Switch />
					</Form.Item>
				</Form>
			</Modal>
		</Container>
	);
};

const Container = styled.div`
	width: 100%;
	padding: 20px;
`;

const Header = styled.div`
	margin-bottom: 16px;
`;

const Pager = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 12px;
	margin-top: 16px;
`;
